import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { interval, Observable, ReplaySubject, Subject, Subscriber, Subscription, TeardownLogic, throwError } from 'rxjs';
import { catchError, switchMap, takeUntil, takeWhile, timeout } from 'rxjs/operators';

import { RestBuilder } from '../../services/helpers/rest.builder';
import {
  IChatMessageStatus,
  IChatSessionPage,
  ISendChatMessageRequest,
  ISendChatMessageResponse,
} from '../../../../shared/interfaces/ai-chat.interface';
import { ApiHeaderOption } from '../../../../shared/constants/api';
import { IChatV2StreamEvent, SSE_EVENT_TYPES } from '../../../../shared/interfaces/ai-executor.interface';
import { AuthService } from '../../services/auth.service';
import { SseService } from '../../services/sse.service';

export const THREE_MINUTES_MS: number = 180000; // 3 minutes default
export const TIMEOUT_DURATION: HttpContextToken<number> = new HttpContextToken<number>((): number => THREE_MINUTES_MS);

export interface IChatMessagesState {
  messages: ISendChatMessageResponse[];
  pendingUserMessage: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AiChatService {
  private readonly restBuilder: RestBuilder;
  private stopPolling$: Subject<void> = new Subject<void>();
  public readonly sessionTitleUpdated$: ReplaySubject<{ sessionId: string; title: string }> = new ReplaySubject<{
    sessionId: string;
    title: string;
  }>(1);

  constructor(
    private httpClient: HttpClient,
    private sseService: SseService,
    private authService: AuthService,
  ) {
    this.restBuilder = new RestBuilder();
  }

  private chat(id?: string): RestBuilder {
    return this.restBuilder.create().ai().chat(id);
  }

  public sendMessage(
    sendChatMessageRequest: ISendChatMessageRequest,
    timeoutMs: number = THREE_MINUTES_MS,
  ): Observable<ISendChatMessageResponse> {
    const context: HttpContext = new HttpContext().set(TIMEOUT_DURATION, timeoutMs);

    return this.httpClient
      .post<ISendChatMessageResponse>(this.chat().getApiUrl(), sendChatMessageRequest, {
        headers: { [ApiHeaderOption.skipLoader]: 'true' },
        context: context,
      })
      .pipe(
        timeout(timeoutMs),
        catchError((error: any) => {
          if (error.name === 'TimeoutError') {
            return throwError({
              status: 504, // Gateway Timeout
              message: 'ai_request_timed_out',
            });
          }
          return throwError(error);
        }),
      );
  }

  public setRating(messageId: string, rating: number, sessionId?: string): Observable<unknown> {
    const url: string = this.chat(messageId).rating().getApiUrl();

    return this.httpClient.put(url, { rating, sessionId });
  }

  public unsetRating(messageId: string, sessionId?: string): Observable<unknown> {
    const url: string = this.chat(messageId).rating().getApiUrl();

    return this.httpClient.delete(url, { body: { sessionId } });
  }

  public getMessagesBySessionId(sessionId: string, skipLoader: boolean = false): Observable<ISendChatMessageResponse[]> {
    const url: string = this.chat().session(sessionId).getApiUrl();

    return this.httpClient.get<ISendChatMessageResponse[]>(url, {
      headers: { [ApiHeaderOption.skipLoader]: skipLoader ? 'true' : 'false' },
    });
  }

  public getMessagesWithPolling(sessionId: string): Observable<IChatMessagesState> {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.stopPolling$ = new Subject<void>();

    return this.getMessagesBySessionId(sessionId).pipe(
      switchMap((initialResult: ISendChatMessageResponse[]): Observable<IChatMessagesState> => {
        const sorted: ISendChatMessageResponse[] = initialResult.sort(
          (a: ISendChatMessageResponse, b: ISendChatMessageResponse): number =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

        const responsePending: boolean =
          sorted.length > 0 &&
          (sorted[0].status === IChatMessageStatus.pending || sorted[0].status === IChatMessageStatus.processing);

        const initialState: IChatMessagesState = responsePending
          ? { messages: sorted.slice(1), pendingUserMessage: sorted[0].message }
          : { messages: sorted, pendingUserMessage: null };

        if (!responsePending) {
          return new Observable<IChatMessagesState>((stateEmitter: Subscriber<IChatMessagesState>): void => {
            stateEmitter.next(initialState);
            stateEmitter.complete();
          });
        }

        return new Observable<IChatMessagesState>((stateEmitter: Subscriber<IChatMessagesState>): TeardownLogic => {
          stateEmitter.next(initialState);

          const subscription: Subscription = interval(1000)
            .pipe(
              takeUntil(this.stopPolling$),
              switchMap((): Observable<ISendChatMessageResponse[]> => this.getMessagesBySessionId(sessionId, true)),
              switchMap((result: ISendChatMessageResponse[]): Observable<IChatMessagesState> => {
                const sortedResult: ISendChatMessageResponse[] = result.sort(
                  (a: ISendChatMessageResponse, b: ISendChatMessageResponse): number =>
                    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                );

                const matchingResponse: string =
                  sortedResult.length > 0 &&
                  sortedResult[0].message === initialState.pendingUserMessage &&
                  sortedResult[0].response;

                if (matchingResponse) {
                  this.stopPolling$.next();
                  this.stopPolling$.complete();
                  return new Observable<IChatMessagesState>((subscriber: Subscriber<IChatMessagesState>): void => {
                    subscriber.next({ messages: sortedResult, pendingUserMessage: null });
                    subscriber.complete();
                  });
                }

                return new Observable<IChatMessagesState>((subscriber: Subscriber<IChatMessagesState>): void => {
                  subscriber.next({ messages: sorted.slice(1), pendingUserMessage: sorted[0].message });
                });
              }),
            )
            .subscribe({
              next: (state: IChatMessagesState): void => stateEmitter.next(state),
              error: (error: any): void => stateEmitter.error(error),
              complete: (): void => stateEmitter.complete(),
            });

          return (): void => subscription.unsubscribe();
        });
      }),
    );
  }

  public sendMessageStream(sendChatMessageRequest: ISendChatMessageRequest): Observable<IChatV2StreamEvent> {
    const url: string = this.chat().sse().getApiUrl();
    const token: string | undefined = this.authService?.getAccessTokenFromStorage();
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

    return this.sseService
      .stream<IChatV2StreamEvent>(url, sendChatMessageRequest, Object.keys(SSE_EVENT_TYPES), headers)
      .pipe(
        takeWhile(
          (event: IChatV2StreamEvent) =>
            event.type !== SSE_EVENT_TYPES['executor-success'] && event.type !== SSE_EVENT_TYPES['executor-failure'],
          true,
        ),
      );
  }

  public stopPolling(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
  }

  public deleteChatSession(sessionId: string): Observable<void> {
    return this.httpClient.delete<void>(this.chat().sessions(sessionId).getApiUrl());
  }

  public renameChatSession(sessionId: string, title: string): Observable<void> {
    return this.httpClient.patch<void>(this.chat().sessions(sessionId).getApiUrl(), { title });
  }

  public favoriteSession(sessionId: string, isFavorite: boolean): Observable<void> {
    return this.httpClient.patch<void>(this.chat().sessions(sessionId).favourite().getApiUrl(), { isFavorite });
  }

  public getChatHistory(page: number, pageSize: number, search?: string): Observable<IChatSessionPage> {
    let params: Record<string, string> = { page: String(page), limit: String(pageSize) };
    if (search) {
      params = { ...params, search };
    }
    return this.httpClient.get<IChatSessionPage>(this.chat().sessions().getApiUrl(), { params });
  }
}
